// components/TaxationServices.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";

import bannerTax from '../assets/banner2.jpg'; // Assuming a professional tax/finance image
import { 
  IoMdCheckmarkCircleOutline, 
  IoMdAnalytics, 
  IoMdDocument, 
  IoMdMailOpen, 
  IoMdTrendingUp,
  IoMdBriefcase,
  IoMdCalendar,
  IoMdArrowDropright,
  IoMdCall
} from "react-icons/io";
import { FaCalculator, FaFileInvoiceDollar, FaShieldVirus, FaUniversity } from "react-icons/fa";

export default function TaxationServices(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  // Grouping the specific services from the ATRIC document
  const taxPillars = [
    {
      id: 1,
      title: "Tax Filing & SETP",
      icon: <IoMdCalendar />,
      description: "Timely preparation, review, and filing of Original and Revised SETP with Tax Authorities.",
      details: "Ensuring all statutory deadlines are met with precision to avoid penalties.",
      accent: "#585858",
      bgLight: "#fffbeb"
    },
    {
      id: 2,
      title: "Corporate Income Tax",
      icon: <FaCalculator />,
      description: "Computation of corporate income tax for audit provisions and Returns of Income (ROI).",
      details: "Expert calculation of tax liabilities aligned with current fiscal regulations.",
      accent: "#1e40af",
      bgLight: "#eff6ff"
    },
    {
      id: 3,
      title: "Tax Health Checks",
      icon: <FaShieldVirus />,
      description: "Comprehensive reviews to check general tax compliance and suggest remedial actions.",
      details: "Proactive identification of risks and implementation of corrective measures.",
      accent: "#047857",
      bgLight: "#f0fdf4"
    },
    {
      id: 4,
      title: "Authority Liaison",
      icon: <IoMdMailOpen />,
      description: "Drafting and submitting responses to correspondences from Tax Authorities timely.",
      details: "Professional representation and communication with regulatory bodies.",
      accent: "#7c3aed",
      bgLight: "#f5f3ff"
    }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => { init(); }, []);

  if (!ready) return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      {getInlineLoader()}
    </div>
  );

  return (
    <div className="bg-white">
      {/* ================= HERO SECTION ================= */}
      <section className="py-5 border-bottom bg-light-subtle" style={{ minHeight: "50vh", display: "flex", alignItems: "center" }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge mb-3 px-3 py-2 fw-bold" style={{ background: "#1e293b", color: "#fff", fontSize: "11px", letterSpacing: "1.5px" }}>
                PROACTIVE TAX PLANNING
              </span>
              <h1 className="display-4 fw-bold text-dark mb-4">
                Driving Business <span style={{ color: "#292929" }}>Sustainability</span> through Compliance.
              </h1>
              <p className="lead text-muted mb-4">
                ATRIC provides end-to-end taxation services designed to mitigate risk and fuel growth through strategic compliance and timely reporting.
              </p>
              <button 
                className="btn btn-primary btn-lg px-4 fw-bold rounded-0"
                onClick={() => appContext.navTo({ item: 'contact_us', service: 'Taxation' })}
              >
                Get a Tax Health Check
              </button>
            </div>
            <div className="col-lg-6 d-none d-lg-block">
               <div className="ps-lg-5">
                  <div className="p-4 bg-white border shadow-sm rounded-4 position-relative">
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div className="bg-primary p-3 rounded-3 text-warning"><FaFileInvoiceDollar size={30} /></div>
                      <div>
                        <h6 className="mb-0 fw-bold">Electronic Filing</h6>
                        <small className="text-muted">Certification of ROI via Authorities Portal</small>
                      </div>
                    </div>
                    <hr />
                    <div className="small text-secondary">
                      Our experts handle the certification and filing of Returns of Income (ROI) directly through the Tax Authority portals.
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CORE SERVICES GRID ================= */}
      <section className="py-5">
        <div className="container py-5">
          <div className="row g-4">
            {taxPillars.map((pillar) => (
              <div className="col-md-6" key={pillar.id}>
                <div className="p-4 h-100 border-start border-4 transition-all hover-shadow" 
                     style={{ borderColor: pillar.accent, background: "white", transition: "0.3s" }}>
                  <div className="d-flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="p-3 rounded-3" style={{ backgroundColor: pillar.bgLight, color: pillar.accent }}>
                        {pillar.icon}
                      </div>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-2">{pillar.title}</h4>
                      <p className="fw-medium text-dark mb-2" style={{ fontSize: "1.05rem" }}>{pillar.description}</p>
                      <p className="text-muted small">{pillar.details}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TAX COMPLIANCE BANNER ================= */}
      <section className="py-5" style={{ background: "#1e293b" }}>
        <div className="container text-center text-white py-4">
          <h2 className="fw-bold mb-4">Navigate Complexity with Confidence</h2>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="d-flex flex-wrap justify-content-center gap-4">
                <div className="d-flex align-items-center gap-2">
                  <IoMdCheckmarkCircleOutline className="text-warning" /> <span>Original & Revised SETP</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <IoMdCheckmarkCircleOutline className="text-warning" /> <span>Audit Provisioning</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <IoMdCheckmarkCircleOutline className="text-warning" /> <span>ROI Certification</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-5 bg-white border-top">
        <div className="container py-5 text-center">
          <h3 className="fw-bold mb-3">Ensure Your Tax Compliance Today</h3>
          <p className="text-muted mb-4">Strategic tax planning is a drive for business growth. Let our consultants handle the numbers.</p>
          <div className="d-flex justify-content-center gap-3">
             <button className="btn btn-dark btn-lg px-5 rounded-0" onClick={() => appContext.navTo({ item: 'contact_us' })}>
               Book Consultation
             </button>
             <a href="tel:+255717918808" className="btn btn-outline-dark btn-lg px-4 rounded-0 d-flex align-items-center gap-2">
               <IoMdCall /> +255 717 918 808
             </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}